<template>
  <div class="page-container">
    <div class="page-content">
      <!-- Page Header -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-gray-900">
          {{ getMovementTitle() }}
        </h1>
        <p class="text-gray-600 mt-2">
          {{ getMovementDescription() }}
        </p>
      </div>

      <!-- Error Alert -->
      <div v-if="error" class="alert alert-error mb-6">
        <span>{{ error }}</span>
        <button @click="clearError" class="btn btn-sm btn-ghost">×</button>
      </div>

      <!-- Form Container -->
      <div class="form-container">
        <div class="card">
          <div class="card-header">
            <h2 class="text-xl font-semibold text-gray-900">{{ t('movements.cardHeader') }}</h2>
          </div>
          <div class="card-body">
            <form @submit.prevent="handleSubmit" class="space-y-6">
              <!-- Two Column Grid for Main Fields -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <!-- Left Column -->
                <div class="space-y-6">
                  <!-- Movement Type -->
                  <div class="form-group">
                    <label class="form-label">
                      {{ t('movements.labelType') }}
                    </label>
                    <select
                      v-model="formData.type"
                      class="select"
                      required
                    >
                      <option value="">{{ t('movements.selectType') }}</option>
                      <option value="in">{{ t('movements.optionIn') }}</option>
                      <option value="out">{{ t('movements.optionOut') }}</option>
                      <option value="transfer">{{ t('movements.optionTransfer') }}</option>
                      <option value="adjust">{{ t('movements.optionAdjustment') }}</option>
                    </select>
                  </div>

                  <!-- Warehouse -->
                  <div class="form-group">
                    <label class="form-label">
                      {{ formData.type === 'transfer' ? t('movements.labelFromWarehouse') : t('movements.labelWarehouse') }}
                    </label>
                    <select
                      v-model="formData.warehouseId"
                      class="select"
                      required
                    >
                      <option value="">{{ t('movements.selectWarehouse') }}</option>
                      <option
                        v-for="warehouse in warehouses"
                        :key="warehouse.id"
                        :value="warehouse.id"
                      >
                        {{ warehouse.name }}
                      </option>
                    </select>
                  </div>

                  <!-- Product -->
                  <div class="form-group">
                    <label class="form-label">
                      {{ t('movements.labelProduct') }}
                    </label>
                    <div class="flex gap-2">
                      <select
                        v-model="formData.productId"
                        class="select flex-1"
                        required
                      >
                        <option value="">{{ t('movements.selectProduct') }}</option>
                        <option
                          v-for="product in products"
                          :key="product.id"
                          :value="product.id"
                        >
                          {{ product.name }} ({{ product.sku }})
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                <!-- Right Column -->
                <div class="space-y-6">
                  <!-- Quantity -->
                  <div class="form-group">
                    <label class="form-label">
                      {{ t('movements.labelQuantity') }}
                    </label>
                    <div class="input-with-addon">
                      <input
                        v-model.number="formData.quantity"
                        type="number"
                        step="1"
                        min="1"
                        class="input"
                        :class="{ 'input-error': quantityError || quantityDecimalError }"
                        :placeholder="t('movements.placeholderQuantity')"
                        required
                      />
                      <span class="input-addon-right">{{ t('common.unit') }}</span>
                    </div>
                    <p v-if="quantityError" class="mt-1 text-sm text-red-600">
                      {{ t('movements.errorQtyZero') }}
                    </p>
                    <p v-else-if="quantityDecimalError" class="mt-1 text-sm text-red-600">
                      {{ t('movements.errorQtyWhole') }}
                    </p>
                  </div>

                  <!-- Unit Cost -->
                  <div class="form-group">
                    <label class="form-label">
                      {{ t('movements.labelUnitCost') }}
                    </label>
                    <div class="relative">
                      <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        v-model.number="formData.unitCost"
                        type="number"
                        step="0.01"
                        min="0"
                        class="input pl-8"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <!-- Unit Price -->
                  <div class="form-group">
                    <label class="form-label">
                      {{ t('movements.labelUnitPrice') }}
                    </label>
                    <div class="relative">
                      <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        v-model.number="formData.unitPrice"
                        type="number"
                        step="0.01"
                        min="0"
                        class="input pl-8"
                        :class="{ 'input-error': priceBelowCostError }"
                        placeholder="0.00"
                      />
                    </div>
                    <p v-if="priceBelowCostError" class="mt-1 text-sm text-red-600">
                      {{ t('movements.errorPriceLessThanCost', { cost: formData.unitCost }) }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Destination Warehouse (for transfers) - Full Width -->
              <div v-if="formData.type === 'transfer'" class="form-group">
                <label class="form-label">
                  {{ t('movements.labelToWarehouse') }}
                </label>
                <select
                  v-model="formData.destinationWarehouseId"
                  class="select"
                  required
                >
                  <option value="">{{ t('movements.selectDestWarehouse') }}</option>
                  <option
                    v-for="warehouse in warehouses"
                    :key="warehouse.id"
                    :value="warehouse.id"
                    :disabled="warehouse.id === formData.warehouseId"
                  >
                    {{ warehouse.name }}
                  </option>
                </select>
              </div>

              <!-- Reference - Full Width -->
              <div class="form-group">
                <label class="form-label">
                  {{ t('movements.labelReference') }}
                </label>
                <input
                  v-model="formData.reference"
                  type="text"
                  class="input"
                  :placeholder="t('movements.placeholderReference')"
                />
              </div>

              <!-- Reason (for adjustments) - Full Width -->
              <div v-if="formData.type === 'adjust'" class="form-group">
                <label class="form-label">
                  {{ t('movements.labelAdjustmentReason') }}
                </label>
                <select
                  v-model="formData.reason"
                  class="select"
                  required
                >
                  <option value="">{{ t('movements.selectReason') }}</option>
                  <option value="cycle_count">{{ t('movements.reasonCycleCount') }}</option>
                  <option value="damage">{{ t('movements.reasonDamage') }}</option>
                  <option value="theft">{{ t('movements.reasonTheft') }}</option>
                  <option value="expired">{{ t('movements.reasonExpired') }}</option>
                  <option value="other">{{ t('movements.reasonOther') }}</option>
                </select>
              </div>

              <!-- Notes - Full Width -->
              <div class="form-group">
                <label class="form-label">
                  {{ t('movements.labelNotes') }}
                </label>
                <textarea
                  v-model="formData.notes"
                  class="textarea"
                  rows="4"
                  :placeholder="t('movements.placeholderNotes')"
                ></textarea>
              </div>

              <!-- Action Buttons -->
              <div class="flex justify-end gap-3 pt-4">
                <router-link to="/inventory" class="btn btn-secondary">
                  {{ t('common.cancel') }}
                </router-link>
                <button
                  type="submit"
                  :disabled="loading || !isFormValid"
                  class="btn btn-primary"
                >
                  <svg v-if="!loading" class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"></path>
                  </svg>
                  {{ loading ? t('common.processing') : t('common.save') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMovementStore } from '../../stores/useMovementStore';
import { useProductStore } from '../../stores/useProductStore';
import { useWarehouseStore } from '../../stores/useWarehouseStore';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const movementStore = useMovementStore();
const productStore = useProductStore();
const warehouseStore = useWarehouseStore();

const formData = ref({
  type: '',
  warehouseId: '',
  productId: '',
  quantity: undefined as number | undefined,
  unitCost: undefined as number | undefined,
  unitPrice: undefined as number | undefined,
  reference: '',
  notes: '',
  destinationWarehouseId: '',
  reason: ''
});

const loading = ref(false);
const error = ref<string | null>(null);

const warehouses = computed(() => warehouseStore.warehouses);
const products = computed(() => productStore.products);

const quantityError = computed(
  () =>
    typeof formData.value.quantity === 'number' &&
    !isNaN(formData.value.quantity) &&
    formData.value.quantity <= 0
);

const quantityDecimalError = computed(
  () =>
    typeof formData.value.quantity === 'number' &&
    !isNaN(formData.value.quantity) &&
    formData.value.quantity > 0 &&
    !Number.isInteger(formData.value.quantity)
);

const priceBelowCostError = computed(
  () =>
    typeof formData.value.unitCost === 'number' &&
    typeof formData.value.unitPrice === 'number' &&
    formData.value.unitPrice < formData.value.unitCost
);

const isFormValid = computed(() => {
  const baseValid =
    formData.value.type &&
    formData.value.warehouseId &&
    formData.value.productId &&
    typeof formData.value.quantity === 'number' &&
    formData.value.quantity > 0 &&
    Number.isInteger(formData.value.quantity) &&
    !priceBelowCostError.value;

  if (formData.value.type === 'transfer') {
    return baseValid && formData.value.destinationWarehouseId;
  }

  if (formData.value.type === 'adjust') {
    return baseValid && formData.value.reason;
  }

  return baseValid;
});

const getMovementTitle = () => {
  const type = formData.value.type;
  if (!type) return t('movements.titleDefault');
  const titles: Record<string, string> = {
    'in': t('movements.titleIn'),
    'out': t('movements.titleOut'),
    'transfer': t('movements.titleTransfer'),
    'adjust': t('movements.titleAdjustment')
  };
  return titles[type] || t('movements.titleDefault');
};

const getMovementDescription = () => {
  const type = formData.value.type;
  if (!type) return t('movements.descDefault');
  const descriptions: Record<string, string> = {
    'in': t('movements.descIn'),
    'out': t('movements.descOut'),
    'transfer': t('movements.descTransfer'),
    'adjust': t('movements.descAdjustment')
  };
  return descriptions[type] || '';
};

const handleSubmit = async () => {
  if (!isFormValid.value) return;

  loading.value = true;
  error.value = null;

  try {
    const refTypeMap = { in: 'PO', out: 'SO', transfer: 'TO', adjust: 'ADJ' } as const;
    const dto = {
      ...formData.value,
      type: formData.value.type as 'in' | 'out' | 'transfer' | 'adjust',
      reference: formData.value.reference
        ? { type: refTypeMap[formData.value.type as keyof typeof refTypeMap], number: formData.value.reference }
        : undefined,
    };
    await movementStore.registerMovement(dto as any);
    router.push('/inventory');
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to register movement';
    console.error('Error registering movement:', err);
  } finally {
    loading.value = false;
  }
};

const clearError = () => { error.value = null; };

onMounted(async () => {
  await Promise.all([
    productStore.fetchProducts(),
    warehouseStore.fetchWarehouses(),
  ]);
});

watch(() => formData.value.type, (newType) => {
  if (newType !== 'transfer') formData.value.destinationWarehouseId = '';
  if (newType !== 'adjust') formData.value.reason = '';
});
</script>
